/* eslint-disable jsx-a11y/anchor-has-content */
import { useCallback } from "react";
import cn from "../../utils/styleHelpers";
import useObjectDrawerStore, { type ObjectDrawerType } from "../../store/drawer/objectDrawerStore";

type HeRef = { type: string; id: string };

function parseHeRef(href?: string): HeRef | null {
  if (!href) return null;
  try {
    const url = new URL(href);
    if (url.protocol !== "he:") return null;
    const type = decodeURIComponent(url.hostname || "");
    const id = decodeURIComponent((url.pathname || "").replace(/^\/+/, ""));
    if (!type || !id) return null;
    return { type, id };
  } catch {
    return null;
  }
}

/** Map he: protocol type strings to ObjectDrawerType */
function resolveObjectType(rawType: string): ObjectDrawerType | null {
  const t = rawType.toLowerCase();
  if (t === "account") return "accounts";
  if (t === "user") return "users";
  // Notes get their own dedicated drawer
  if (t === "note" || t === "notes") return "note";
  // Meeting recordings open the unified meeting drawer
  if (t === "meeting_recording" || t === "meeting_recordings" || t === "recording") return "meeting";
  // All other custom object slugs map to "custom-object"
  const customObjectAliases = new Set([
    "conversation_message", "conversation_messages",
  ]);
  if (customObjectAliases.has(t)) return "custom-object";
  const supported: ObjectDrawerType[] = ["email", "meeting", "doc", "custom-object", "concept", "task"];
  if (supported.includes(t as ObjectDrawerType)) return t as ObjectDrawerType;
  return null;
}

const HeRefLink = ({ href, children, className, title }: any) => {
  const openDrawer = useObjectDrawerStore((s) => s.openDrawer);

  const ref = parseHeRef(href);

  const handleHeClick = useCallback(() => {
    if (!ref) return;
    const objectType = resolveObjectType(ref.type);
    if (!objectType) return;
    openDrawer({ objectType, objectId: ref.id });
  }, [openDrawer, ref]);

  if (!href) {
    return (
      <span
        title={title}
        className={cn(
          className,
          "text-gray-900 font-medium underline decoration-gray-300 cursor-default"
        )}
      >
        {children}
      </span>
    );
  }

  if (ref) {
    return (
      <button
        type="button"
        onClick={handleHeClick}
        title={title}
        className={cn(
          className,
          "inline-flex items-center font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 px-1 py-0.5 rounded cursor-pointer transition-colors"
        )}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      title={title}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        className,
        "cursor-pointer text-indigo-600 font-medium decoration-indigo-300 hover:text-indigo-700 transition-all"
      )}
    >
      {children}
    </a>
  );
};

export default HeRefLink;
